import React from 'react'
import {
  Box,
  Chip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material'
import {
  Place,
  PhotoCamera,
} from '@mui/icons-material'
import { Vehicle, TransporterPackageWithVehicles } from '@otw/models'
import { useAdminTransporter } from '@/hooks/admin/useAdminTransporter'
import { useTransporterPhotoStatus } from '@/hooks/useTransporterPhotoStatus'
import { usePhotoTaskTemplates } from '@/hooks/admin/usePhotoTaskTemplates'
import { useRepairShopLocations } from '@/hooks/inventory/useRepairShopLocations'

interface StatusBarProps {
  vehicle: Vehicle
  package: TransporterPackageWithVehicles
}

const StatusBar: React.FC<StatusBarProps> = ({
  vehicle,
  package: pkg
}) => {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  
  const { 
    hasVehiclePickupLocation, 
    hasVehicleDropoffLocation,
    hasVehicleInterimLocation
  } = useAdminTransporter()
  
  // Get repair shop locations
  const { repairShopLocations } = useRepairShopLocations()

  // Helper function to check if a pickup location is a repair shop
  const isPickupLocationRepairShop = (pickup: any): boolean => {
    if (!pickup || !repairShopLocations || repairShopLocations.length === 0) {
      return false;
    }
    
    const normalize = (str: string | undefined) => (str || '').trim().toLowerCase();
    const pickupAddress = normalize(pickup.address);
    const pickupCity = normalize(pickup.city);
    const pickupState = normalize(pickup.state);
    const pickupZip = normalize(pickup.zip);
    
    // Check if pickup location matches any repair shop by address fields
    return repairShopLocations.some(shop => {
      const shopAddress = normalize(shop.address);
      const shopCity = normalize(shop.city);
      const shopState = normalize(shop.state);
      const shopZip = normalize(shop.zip);
      
      return shopAddress === pickupAddress &&
             shopCity === pickupCity &&
             shopState === pickupState &&
             shopZip === pickupZip;
    });
  };

  const hasPickupData = hasVehiclePickupLocation(pkg, vehicle.id)
  const pickup = pkg.pickup_locations?.[vehicle.id]
  const isPickupRepairShop = pickup ? isPickupLocationRepairShop(pickup) : false
  const hasDropoffData = hasVehicleDropoffLocation(pkg, vehicle.id)
  const dropoff = pkg.dropoff_locations?.[vehicle.id]
  const hasInterimData = hasVehicleInterimLocation(pkg, vehicle.id)
  // Handle interim_locations as array - use latest (last) location
  const interimLocation = pkg.interim_locations?.[vehicle.id]
  const interim = interimLocation ? (Array.isArray(interimLocation) ? interimLocation[interimLocation.length - 1] : interimLocation) : undefined
  const isCompleted = pkg.status === 'COMPLETED'
  
  // Get real-time media status (photos + videos) using the hook
  const vehicleIds = React.useMemo(() => [vehicle.id], [vehicle.id])
  const { getMediaCount } = useTransporterPhotoStatus(pkg.id, vehicleIds)
  
  // Get photo task templates to determine required media
  const { getTemplateByFlow } = usePhotoTaskTemplates()
  
  // Get required media from templates (using company-specific template if available)
  const preDeliveryTemplate = getTemplateByFlow('preDelivery', pkg.company_id)
  const postDeliveryTemplate = getTemplateByFlow('postDelivery', pkg.company_id)
  const requiredPreDeliveryMedia = preDeliveryTemplate?.tasks?.filter(task => task.active).length || 0
  const requiredPostDeliveryMedia = postDeliveryTemplate?.tasks?.filter(task => task.active).length || 0
  
  // Get media counts (photos + videos) directly for this specific package-vehicle combination
  const preDeliveryMedia = getMediaCount(pkg.id, vehicle.id, 'preDelivery')
  const postDeliveryMedia = getMediaCount(pkg.id, vehicle.id, 'postDelivery')
  
  const preDeliveryMediaCountDisplay = `${preDeliveryMedia.total}/${requiredPreDeliveryMedia}`
  const postDeliveryMediaCountDisplay = `${postDeliveryMedia.total}/${requiredPostDeliveryMedia}`

  // Get vehicle status from the new status structure (handled by listener)
  const submissionStatuses = pkg.vehicle_status?.[vehicle.id] || []
  const latestStatus = submissionStatuses.length > 0 ? submissionStatuses[submissionStatuses.length - 1] : null
  
  // Status progression is now handled by the photosCompletedListener
  // For repair status, treat it the same as "In Transit" for all indicators except the progress bar
  const hasPreDeliverySubmitted = latestStatus?.status === 'In Transit' || latestStatus?.status === 'Completed' || latestStatus?.status === 'Repair'
  const hasPostDeliverySubmitted = latestStatus?.status === 'Completed'
  const isInRepair = latestStatus?.status === 'Repair'
  
  // Use real-time media status for visual indicators
  const hasPreDeliveryMedia = preDeliveryMedia.total > 0
  const hasPostDeliveryMedia = postDeliveryMedia.total > 0
  
  // Check if media requirements are met (regardless of status)
  const preDeliveryComplete = preDeliveryMedia.total >= requiredPreDeliveryMedia
  const postDeliveryComplete = postDeliveryMedia.total >= requiredPostDeliveryMedia

  // Determine if we should show media status bars
  const shouldShowMediaStatus = pkg.status !== 'DRAFT'

  return (
    <Box sx={{
      display: 'flex',
      flexDirection: 'column',
      gap: 0.5,
      mb: 1,
      position: 'relative',
      p: 0.5,
      borderRadius: 1,
      backgroundColor: 'grey.50',
      border: '1px solid',
      borderColor: 'grey.200'
    }}>
      {/* Mobile Layout: Vertical stack with progress bar */}
      {isMobile ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          {/* Pickup Location */}
          <Chip
            icon={<Place fontSize="small" />}
            label={hasPickupData 
              ? `Pickup: ${pickup?.city}, ${pickup?.state}`
              : `Pickup: Not defined`
            }
            size="small"
            variant={hasPreDeliverySubmitted && !isPickupRepairShop && hasPickupData ? "filled" : "outlined"}
            color={isPickupRepairShop ? "error" : hasPickupData ? (hasPreDeliverySubmitted ? "success" : "primary") : "error"}
            sx={{ 
              fontSize: '0.7rem', 
              height: 20,
              fontWeight: 500,
              transition: 'all 0.3s ease-in-out',
              ...(hasPreDeliverySubmitted && !isPickupRepairShop && hasPickupData && {
                backgroundColor: 'success.main',
                color: 'white',
                boxShadow: '0 2px 4px rgba(76, 175, 80, 0.3)',
                '& .MuiChip-label': {
                  color: 'white'
                },
                '& .MuiChip-icon': {
                  color: 'white'
                }
              }),
              ...(isPickupRepairShop && {
                borderColor: 'error.main',
                color: 'error.main',
                '& .MuiChip-icon': {
                  color: 'error.main'
                }
              })
            }}
          />
          
          {/* Repair Location (if exists) */}
          {hasInterimData && (
            <Chip
              icon={<Place fontSize="small" />}
              label={`Repair: ${interim?.city}, ${interim?.state}`}
              size="small"
              variant="outlined"
              color="secondary"
              sx={{ 
                fontSize: '0.7rem', 
                height: 20,
                fontWeight: 500,
                borderColor: 'secondary.main',
                color: 'secondary.main',
                '& .MuiChip-icon': {
                  color: 'secondary.main'
                }
              }}
            />
          )}
          
          {/* Dropoff Location */}
          <Chip
            icon={<Place fontSize="small" />}
            label={hasDropoffData 
              ? `Dropoff: ${dropoff?.city}, ${dropoff?.state}`
              : 'Dropoff: Not defined'
            }
            size="small"
            variant={hasPostDeliverySubmitted ? "filled" : "outlined"}
            color={hasDropoffData ? (hasPostDeliverySubmitted ? "success" : "primary") : "error"}
            sx={{ 
              fontSize: '0.7rem', 
              height: 20,
              fontWeight: 500,
              transition: 'all 0.3s ease-in-out',
              ...(hasPostDeliverySubmitted && {
                backgroundColor: 'success.main',
                color: 'white',
                boxShadow: '0 2px 4px rgba(76, 175, 80, 0.3)',
                '& .MuiChip-label': {
                  color: 'white'
                },
                '& .MuiChip-icon': {
                  color: 'white'
                }
              })
            }}
          />
        </Box>
      ) : (
        <>
          {/* Media Upload Status Bars (Photos + Videos) - Desktop only */}
          {shouldShowMediaStatus && (
            <Box sx={{
              display: 'flex',
              gap: 1,
              px: 1,
              py: 0.5
            }}>
              {/* Pre-Delivery Media Status */}
              <Box sx={{
                flex: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                px: 1,
                py: 0.5,
                backgroundColor: 'background.paper',
                borderRadius: 0.5,
                border: '1px solid',
                borderColor: preDeliveryComplete ? 'success.main' : preDeliveryMedia.total > 0 ? 'warning.main' : 'grey.300',
              }}>
                <Typography variant="caption" sx={{ fontWeight: 500, color: 'text.primary' }}>
                  Pre-Delivery Media
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="caption" sx={{ fontWeight: 600, color: preDeliveryComplete ? 'success.main' : preDeliveryMedia.total > 0 ? 'warning.main' : 'text.primary' }}>
                    {preDeliveryMediaCountDisplay}
                  </Typography>
                  <Box sx={{
                    width: 50,
                    height: 4,
                    backgroundColor: 'grey.300',
                    borderRadius: 2,
                    overflow: 'hidden',
                    position: 'relative'
                  }}>
                    <Box sx={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      height: '100%',
                      width: requiredPreDeliveryMedia > 0 ? `${Math.round((preDeliveryMedia.total / requiredPreDeliveryMedia) * 100)}%` : '0%',
                      backgroundColor: preDeliveryComplete ? 'success.main' : preDeliveryMedia.total > 0 ? 'warning.main' : 'grey.400',
                      borderRadius: 2,
                      transition: 'all 0.3s ease-in-out'
                    }} />
                  </Box>
                  <Typography variant="caption" sx={{ fontWeight: 500, color: 'text.secondary', minWidth: '30px' }}>
                    {requiredPreDeliveryMedia > 0 ? Math.round((preDeliveryMedia.total / requiredPreDeliveryMedia) * 100) : 0}%
                  </Typography>
                </Box>
              </Box>

              {/* Post-Delivery Media Status */}
              <Box sx={{
                flex: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                px: 1,
                py: 0.5,
                backgroundColor: 'background.paper',
                borderRadius: 0.5,
                border: '1px solid',
                borderColor: postDeliveryComplete ? 'success.main' : postDeliveryMedia.total > 0 ? 'warning.main' : 'grey.300'
              }}>
                <Typography variant="caption" sx={{ fontWeight: 500, color: 'text.primary' }}>
                  Post-Delivery Media
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="caption" sx={{ fontWeight: 600, color: postDeliveryComplete ? 'success.main' : postDeliveryMedia.total > 0 ? 'warning.main' : 'text.primary' }}>
                    {postDeliveryMediaCountDisplay}
                  </Typography>
                  <Box sx={{
                    width: 50,
                    height: 4,
                    backgroundColor: 'grey.300',
                    borderRadius: 2,
                    overflow: 'hidden',
                    position: 'relative'
                  }}>
                    <Box sx={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      height: '100%',
                      width: requiredPostDeliveryMedia > 0 ? `${Math.round((postDeliveryMedia.total / requiredPostDeliveryMedia) * 100)}%` : '0%',
                      backgroundColor: postDeliveryComplete ? 'success.main' : postDeliveryMedia.total > 0 ? 'warning.main' : 'grey.400',
                      borderRadius: 2,
                      transition: 'all 0.3s ease-in-out'
                    }} />
                  </Box>
                  <Typography variant="caption" sx={{ fontWeight: 500, color: 'text.secondary', minWidth: '30px' }}>
                    {requiredPostDeliveryMedia > 0 ? Math.round((postDeliveryMedia.total / requiredPostDeliveryMedia) * 100) : 0}%
                  </Typography>
                </Box>
              </Box>
            </Box>
          )}

          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.5
          }}>
            {/* Pickup Location Chip */}
            <Chip
              icon={<Place fontSize="small" />}
              label={hasPickupData 
                ? `Pickup: ${pickup?.city}, ${pickup?.state}`
                : `Pickup: Not defined`
              }
              size="small"
              variant={hasPreDeliverySubmitted && !isPickupRepairShop && hasPickupData ? "filled" : "outlined"}
              color={isPickupRepairShop ? "error" : hasPickupData ? (hasPreDeliverySubmitted ? "success" : "primary") : "error"}
              sx={{ 
                fontSize: '0.7rem', 
                height: 20,
                fontWeight: 500,
                transition: 'all 0.3s ease-in-out',
                ...(hasPreDeliverySubmitted && !isPickupRepairShop && hasPickupData && {
                  backgroundColor: 'success.main',
                  color: 'white',
                  boxShadow: '0 2px 4px rgba(76, 175, 80, 0.3)',
                  '& .MuiChip-label': {
                    color: 'white'
                  },
                  '& .MuiChip-icon': {
                    color: 'white'
                  }
                }),
                ...(isPickupRepairShop && {
                  borderColor: 'error.main',
                  color: 'error.main',
                  '& .MuiChip-icon': {
                    color: 'error.main'
                  }
                })
              }}
            />
            
            {/* Animated Progress Bar */}
            <Box sx={{ 
              flex: 1,
              height: 4,
              backgroundColor: 'grey.300',
              borderRadius: 2,
              position: 'relative',
              overflow: 'hidden',
              mx: 0.5
            }}>
              <Box sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                height: '100%',
                width: isInRepair ? '100%' : hasPostDeliverySubmitted ? '100%' : hasPreDeliverySubmitted ? '50%' : '0%',
                backgroundColor: isInRepair ? 'error.main' : hasPostDeliverySubmitted ? 'success.main' : hasPreDeliverySubmitted ? 'primary.main' : 'transparent',
                borderRadius: 2,
                transition: 'all 0.6s ease-in-out',
                ...(preDeliveryMedia.total >= requiredPreDeliveryMedia && requiredPreDeliveryMedia > 0 && !hasPostDeliverySubmitted && !isInRepair && {
                  backgroundColor: 'primary.main',
                  animation: 'progressFill 2s ease-out infinite',
                  animationDelay: '0s',
                })
              }} />
            </Box>
            
            {/* Dropoff Location Chip */}
            <Chip
              icon={<Place fontSize="small" />}
              label={hasDropoffData 
                ? `Dropoff: ${dropoff?.city}, ${dropoff?.state}`
                : 'Dropoff: Not defined'
              }
              size="small"
              variant={hasPostDeliverySubmitted ? "filled" : "outlined"}
              color={hasDropoffData ? (hasPostDeliverySubmitted ? "success" : "primary") : "error"}
              sx={{ 
                fontSize: '0.7rem', 
                height: 20,
                fontWeight: 500,
                transition: 'all 0.3s ease-in-out',
                ...(hasPostDeliverySubmitted && {
                  backgroundColor: 'success.main',
                  color: 'white',
                  boxShadow: '0 2px 4px rgba(76, 175, 80, 0.3)',
                  '& .MuiChip-label': {
                    color: 'white'
                  },
                  '& .MuiChip-icon': {
                    color: 'white'
                  }
                })
              }}
            />
          </Box>

          {/* Interim Location Chip - Show when interim location exists */}
          {hasInterimData && (
            <Box sx={{ mt: 0.5, display: 'flex', justifyContent: 'center' }}>
              <Chip
                icon={<Place fontSize="small" />}
                label={`Repair: ${interim?.city}, ${interim?.state}`}
                size="small"
                variant="outlined"
                color="secondary"
                sx={{ 
                  fontSize: '0.7rem', 
                  height: 20,
                  fontWeight: 500,
                  borderColor: 'secondary.main',
                  color: 'secondary.main',
                  '& .MuiChip-icon': {
                    color: 'secondary.main'
                  }
                }}
              />
            </Box>
          )}
        </>
      )}
    </Box>
  )
}

export default StatusBar
